import { useEffect } from "react";
import { useErrorHandler } from "../shared/errorHandler";
import { genericEntityService } from "../../../services/genericEntityService";
import { FieldTypeEnum } from "../../../enums/fieldTypeEnum";
import "./item.css";
import { NumericFieldItemForm } from "./fields/NumericFieldItemForm";
import { BooleanFieldItemForm } from "./fields/BooleanFieldItemForm";
import { TextFieldItemForm } from "./fields/TextFieldItemForm";
import { DatetimeFieldItemForm } from "./fields/DatetimeFieldItemForm";
import { AttachmentFieldItemForm } from "./fields/AttachmentFieldItemForm";
import { RelationFieldItemForm } from "./fields/RelationFieldItemForm";
import { DropdownFieldItemForm } from "./fields/DropdownFieldItemForm";
import { DateFieldItemForm } from "./fields/DateFieldItemForm";
import { IdFieldItemForm } from "./fields/IdFieldItemForm";
import { TimeFieldItemForm } from "./fields/TimeFieldItemForm";
import { ReferenceColumnFieldItemForm } from "./fields/ReferenceColumnFieldItemForm";

export const ItemForm = ({ entity, itemId, itemForm, changeFormCallback, canEdit }) => {
    const { handleHttpError } = useErrorHandler();
    const fields = entity.Fields ?? entity.FieldsMetadata

    useEffect(() => {
        if (itemId !== null) {
            genericEntityService.getItemById(entity.EntityId, itemId)
                .then(function (response) {
                    let values = {};
                    // API returns PascalCase (Fields), handle both cases
                    const fields = response.data.Fields || response.data.fields;
                    const attachments = response.data.Attachments || response.data.attachments || {};

                    // Build a map of field codes to field definitions from entity
                    const fieldTypeDic = entity.Fields?.reduce(
                        (prev, cur) => ({ ...prev, [cur.Code]: cur }), {}
                    ) ?? {};

                    for (const key in fields) {
                        const fieldValue = fields[key];
                        const fieldDef = fieldTypeDic[key];

                        if (fieldValue === null || fieldValue === undefined) {
                            continue;
                        }

                        var value;
                        switch (fieldDef?.Type) {
                            case FieldTypeEnum.Boolean:
                                value = String(fieldValue).toLowerCase() === "true" ? "true" : "false";
                                break;
                            case FieldTypeEnum.Attachment:
                                continue;
                            // case FieldTypeEnum.Dropdown:
                            //     // Ensure dropdown values are numbers (dropdownItemId is int)
                            //     value = typeof fieldValue === 'string' ? Number(fieldValue) : fieldValue;
                            //     break;
                            default:
                                value = fieldValue;
                                break;
                        }
                        values[key] = value;
                    }

                    changeFormCallback({
                        ...itemForm,
                        form: { ...values, ...attachments }
                    });
                })
                .catch(function (error) { handleHttpError(error); })
        } else {
            const defaultValues = fields.reduce((acc, field) => {
                acc[field.Code] = field.DefaultValue ?? null;
                return acc;
            }, {});

            changeFormCallback({
                form: defaultValues,
                errors: {}
            });
        }
    }, [])

    const handleChangeValue = (form) => {
        changeFormCallback(form);
    }

    const getFormField = (field) => {
        switch (field.Type) {
            case FieldTypeEnum.Integer:
                return <NumericFieldItemForm
                    key={field.FieldId}
                    field={field}
                    changeValueCallback={handleChangeValue}
                    isDecimalField={false}
                    isDisabled={!canEdit}
                    itemForm={itemForm}
                />
            case FieldTypeEnum.Decimal:
                return <NumericFieldItemForm
                    key={field.FieldId}
                    field={field}
                    changeValueCallback={handleChangeValue}
                    isDecimalField={true}
                    isDisabled={!canEdit}
                    itemForm={itemForm}
                />
            case FieldTypeEnum.Boolean:
                return <BooleanFieldItemForm
                    key={field.FieldId}
                    field={field}
                    changeValueCallback={handleChangeValue}
                    itemForm={itemForm}
                    isDisabled={!canEdit}
                />
            case FieldTypeEnum.Text:
                return <TextFieldItemForm
                    key={field.FieldId}
                    field={field}
                    isDisabled={!canEdit}
                    changeValueCallback={handleChangeValue}
                    itemForm={itemForm}
                />
            case FieldTypeEnum.Datetime:
                return <DatetimeFieldItemForm
                    key={field.FieldId}
                    field={field}
                    changeValueCallback={handleChangeValue}
                    itemForm={itemForm}
                    isDisabled={!canEdit}
                />
            case FieldTypeEnum.Attachment:
                return <AttachmentFieldItemForm
                    key={field.FieldId}
                    field={field}
                    changeValueCallback={handleChangeValue}
                    itemForm={itemForm}
                    isDisabled={!canEdit}
                />
            case FieldTypeEnum.Currency:
                return <NumericFieldItemForm
                    key={field.FieldId}
                    field={field}
                    changeValueCallback={handleChangeValue}
                    isDecimalField={true}
                    isDisabled={!canEdit}
                    itemForm={itemForm}
                />
            case FieldTypeEnum.Dropdown:
                return <DropdownFieldItemForm
                    key={field.FieldId}
                    field={field}
                    changeValueCallback={handleChangeValue}
                    itemForm={itemForm}
                    isDisabled={!canEdit}
                />
            case FieldTypeEnum.Time:
                return <TimeFieldItemForm
                    key={field.FieldId}
                    field={field}
                    changeValueCallback={handleChangeValue}
                    itemForm={itemForm}
                    isDisabled={!canEdit}
                />
            case FieldTypeEnum.Date:
                return <DateFieldItemForm
                    key={field.FieldId}
                    field={field}
                    changeValueCallback={handleChangeValue}
                    itemForm={itemForm}
                    isDisabled={!canEdit}
                />

            case FieldTypeEnum.Id:
                return <IdFieldItemForm
                    key={field.FieldId}
                    field={field}
                    changeValueCallback={handleChangeValue}
                    itemForm={itemForm}
                    isDisabled={true}
                />

            case FieldTypeEnum.ReferenceColumn:
                return <ReferenceColumnFieldItemForm
                    key={field.FieldId}
                    field={field}
                    entityId={entity.EntityId}
                    changeValueCallback={handleChangeValue}
                    itemForm={itemForm}
                    isDisabled={!canEdit}
                />

            case FieldTypeEnum.Relation:
                return <RelationFieldItemForm
                    key={field.FieldId}
                    field={field}
                    entityId={entity.EntityId}
                    changeValueCallback={handleChangeValue}
                    itemForm={itemForm}
                    isDisabled={!canEdit}
                />
        }
    }

    return (
        <>
            <div className="item-form-grid">
                {
                    fields?.map(f => {
                        return (
                            <div key={f.FieldId} style={{ display: "flex", gridColumnEnd: "span 2" }}>
                                {getFormField(f)}
                            </div>

                        )
                    })

                }
            </div>
        </>
    )
}

